/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { useForm } from "../../provider/Form/FormProvider";
import ValidatorTextField from "../FormFields/ValidatorTextField";
import { emailDefault, isRequired, minStringLength, requiredValidator } from "../../services/validationRules";
import { Box, Button, InputAdornment, Stack, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import React, { useMemo, useState } from "react";
import ButtonLoading from "../Button/ButtonLoading";
import authActionFormStyle from "./authActionFormStyle";
import { Link } from "react-router";
import { AuthActionType } from "@/src/types/AuthActionType";
import { mergeSx } from "@/src/utils/mergeSx";

type AuthActionFormProps = {
  actionType?: AuthActionType;
  setActionType: Function;
  isLoading?: boolean;
  showPostfix?: boolean;
};

export default function AuthActionForm({ actionType, setActionType, isLoading = false, showPostfix = true }: AuthActionFormProps) {
  const [showPassword, setShowPassword] = useState(false);

  const buttonText = useMemo(() => {
    switch (actionType) {
      default:
      case "login":
        return "Jetzt anmelden";
      case "register":
        return "Jetzt starten";
      case "recover":
        return "Passwort zurücksetzen";
    }
  }, [actionType]);

  const prefixText = useMemo(() => {
    switch (actionType) {
      default:
        return "";
      case "register":
        return (
          <Typography sx={authActionFormStyle.text}>
            Neu bei Legalbird und noch keine E-Mail erhalten? Dann legen Sie sich jetzt ein neues Konto an.
            <br />
            <strong>Sie haben schon eine E-Mail erhalten? Dann haben Sie schon ein Konto und müssen dieses nur noch kurz bestätigen.</strong>
            <br />
            Nutzen Sie dazu einfach den Link in der E-Mail.
          </Typography>
        );
      case "recover":
        return (
          <Typography sx={authActionFormStyle.text}>
            Sie haben Ihr Passwort vergessen?
            <br />
            Gerne schicken wir Ihnen einen Link zum Zurücksetzen Ihres Passworts zu
          </Typography>
        );
    }
  }, [actionType]);

  const postfixText = useMemo(() => {
    switch (actionType) {
      default:
      case "login":
        return (
          <Stack
            direction={"row"}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            <Typography>Noch kein Konto?</Typography>
            <Button variant={"text"} onClick={() => setActionType("register")} sx={authActionFormStyle.textButton}>
              Jetzt registrieren
            </Button>
          </Stack>
        );
      case "register":
        return (
          <>
            <Box sx={authActionFormStyle.termsOfServiceText}>
              Ich stimme den{" "}
              <Link to={"/agb"} target="_blank">
                AGB der Legalbird Rechtsanwälte Rechtsanwalts GmbH
              </Link>{" "}
              und den{" "}
              <Link to={"/agb-legalbird"} target="_blank">
                AGB der Legalbird GmbH
              </Link>{" "}
              zu. Die{" "}
              <Link to={"/datenschutz"} target="_blank">
                Datenschutzerklärung der Legalbird Rechtsanwälte Rechtsanwalts GmbH
              </Link>{" "}
              und die{" "}
              <Link to={"/datenschutz-legalbird"} target="_blank">
                Datenschutzerklärung der Legalbird GmbH
              </Link>{" "}
              habe ich zur Kenntnis genommen.
              <br />
            </Box>
            <Stack
              direction={"row"}
              sx={{
                justifyContent: "center",
                alignItems: "center",
                whiteSpace: "nowrap",
              }}
            >
              <Typography>Bereits ein Konto?</Typography>
              <Button variant={"text"} sx={authActionFormStyle.textButton} onClick={() => setActionType("login")}>
                Jetzt anmelden
              </Button>
            </Stack>
          </>
        );
      case "recover":
        return (
          <Stack
            direction={"row"}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            <Typography>Zurück zur</Typography>
            <Button variant={"text"} sx={authActionFormStyle.textButton} onClick={() => setActionType("login")}>
              Anmeldung
            </Button>
          </Stack>
        );
    }
  }, [actionType]);

  const { handleSubmit } = useForm();

  return (
    <form onSubmit={handleSubmit}>
      {prefixText}
      <ValidatorTextField
        label="Ihre E-Mail-Adresse"
        name="email"
        validators={[requiredValidator("Bitte geben Sie Ihre E-Mail-Adresse an"), emailDefault]}
        autoComplete="email"
      />
      {actionType === "login" && (
        <>
          <ValidatorTextField
            type={showPassword ? "text" : "password"}
            label="Passwort"
            name="password"
            validators={[requiredValidator("Bitte geben Sie Ihre Passwort an")]}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Stack
            sx={[
              {
                alignItems: "end",
              },
              authActionFormStyle.recoverPassword,
            ]}
          >
            <Button variant={"text"} onClick={() => setActionType("recover")}>
              Passwort vergessen?
            </Button>
          </Stack>
        </>
      )}
      {actionType === "register" && (
        <>
          <ValidatorTextField
            name="givenName"
            label="Vorname"
            validators={[
              { validator: isRequired, message: "Bitte geben Sie den Vornamen an" },
              { validator: minStringLength, params: [2], message: "Bitte kontrollieren Sie Ihre Eingabe" },
            ]}
            autoComplete="given-name"
          />
          <ValidatorTextField
            name="familyName"
            label="Nachname"
            validators={[
              { validator: isRequired, message: "Bitte geben Sie den Nachnamen an" },
              { validator: minStringLength, params: [2], message: "Bitte kontrollieren Sie Ihre Eingabe" },
            ]}
            autoComplete="family-name"
          />
        </>
      )}
      <ButtonLoading
        sx={mergeSx(authActionFormStyle.submitButton, !showPostfix && authActionFormStyle.submitButtonWithoutPostfix)}
        color="primary"
        variant="contained"
        type="submit"
        fullWidth
        isLoading={isLoading}
      >
        {buttonText}
      </ButtonLoading>
      {showPostfix && postfixText}
    </form>
  );
}
